/**
 * Client side of the server battle service (design D4, port addition): the texts and the small pure helpers the game
 * needs around FWP.net_battle_start / net_battle_end / net_grave_search. A solo battle (career / missions / survival /
 * boss / assault) starts with the backend's `battle.start` (the server applies the start costs to the canonical state
 * and fixes the seed), ends with `battle.finish` (the server re-simulates the recorded input when it has a simulator
 * and applies the rewards of the outcome it accepts) or `battle.forfeit` (the surrender button), and the graves of a
 * won battle are searched with `grave.search`. The game adopts the state every answer carries and shows what the
 * server decided; it never computes a reward itself.
 *
 * As everywhere in the game, the Russian text is the source and the translation key (src/game/runtime/i18n.ts); every
 * literal here has a catalogue entry (tools/i18n/extra.json). Browser-safe: no imports.
 */

/**
 * Per-request timeout of battle.finish: the server may re-simulate the whole battle before it answers (the client
 * library's BATTLE_FINISH_TIMEOUT_MS; the game does not import the client modules, tests/e2e-battle checks they agree).
 */
export const BATTLE_FINISH_TIMEOUT_MS = 90_000;

// ---------------------------------------------------------------- messages

/** Shown while battle.finish runs (the server may be re-simulating the battle). */
export const MSG_BATTLE_CHECK = 'Проверка боя…';
/** The server's re-simulation did not confirm the claim: the screens show the outcome the server applied. */
export const MSG_BATTLE_REJECTED = 'Сервер не подтвердил результат боя.';
/** The battle gave nothing: it was no longer in progress on the server (abandoned, replaced, the progress changed). */
export const MSG_BATTLE_NOT_COUNTED = 'Бой не засчитан.';
/** An assault won, but the zone was not captured (it changed hands during the battle, or the win was not verified). */
export const MSG_ZONE_NOT_CAPTURED = 'Территорию захватить не удалось.';
/** A grave the server would not search (already searched, the battle's graves are gone). */
export const MSG_GRAVE_REFUSED = 'Этот гробик уже не обыскать.';
/** A refused battle start whose reason has no text of its own. */
export const MSG_BATTLE_UNAVAILABLE = 'Этот бой сейчас недоступен.';

/** Texts of the battle.start refusals (VALIDATION_FAILED `reason`, src/game/rules/ops/battle.ts checkBattleStart). */
const START_REFUSALS: Readonly<Record<string, string>> = {
  too_tired: 'Ваши черви слишком устали для боя.',
  level_too_low: 'Для этого боя нужен более высокий уровень.',
  already_won: 'Вы уже победили этого босса.',
  zone_owned: 'Эта территория уже ваша.',
  zone_clan_owned: 'Эта территория принадлежит вашему клану.',
};

/** Short message after the server refused to start a battle (the game has re-loaded the server's state by then). */
export function battleRefusedText(reason: string): string {
  return Object.prototype.hasOwnProperty.call(START_REFUSALS, reason) ? START_REFUSALS[reason] : MSG_BATTLE_UNAVAILABLE;
}

// ---------------------------------------------------------------- calls

/** Why the server did not carry a battle command out: the error code and its machine reason. */
export interface BattleRefusal {
  code: string;
  reason: string;
}

/**
 * What a battle command gave the game: the command's outcome (GameServices.CommandOutcome), or a refusal the game shows
 * (VALIDATION_FAILED: a precondition; INVALID_STATE: the battle is no longer in progress on the server). Every other
 * failure goes to the game's backend bridge (FWP.net_fail: retries, re-authentication, the error splash).
 */
export type BattleCommandAnswer = { outcome: any; refused?: undefined } | { refused: BattleRefusal; outcome?: undefined };

function errorCode(err: any): string {
  return err && typeof err.code === 'string' ? err.code : '';
}

function errorReason(err: any): string {
  return err && err.details && typeof err.details.reason === 'string' ? err.details.reason : '';
}

/**
 * battle.finish answered UNAVAILABLE 'simulation_unavailable': the server could not verify the battle right now (its
 * simulator is busy or restarting) and keeps it pending. For the game's backend bridge that is a busy server
 * (RATE_LIMITED): FWP.net_fail sends the call again after its backoff on the bridge clock ("Сервер перегружен. Повтор
 * через N с…", a click retries at once), and GameServices.command sends it under the same opId, so the server
 * rewards the battle once.
 */
export class SimulationBusyError extends Error {
  readonly code = 'RATE_LIMITED';
  readonly details: { reason: string };
  constructor(readonly cause: unknown) {
    super('The battle cannot be verified right now');
    this.name = 'SimulationBusyError';
    this.details = { reason: 'simulation_unavailable' };
  }
}

/** Runs a battle command: its outcome or a refusal (see BattleCommandAnswer); `verifies`: battle.finish. */
export async function battleCommand(call: Promise<any>, verifies = false): Promise<BattleCommandAnswer> {
  try {
    return { outcome: await call };
  } catch (err) {
    const code = errorCode(err);
    if (code === 'VALIDATION_FAILED' || code === 'INVALID_STATE') return { refused: { code, reason: errorReason(err) } };
    if (verifies && code === 'UNAVAILABLE' && errorReason(err) === 'simulation_unavailable') throw new SimulationBusyError(err);
    throw err;
  }
}

/** The recorded input ends with the surrender button (proc.cancel_fight): the battle is given up, not played out. */
export function battleSurrendered(log: any): boolean {
  const turns: any[] = log && Array.isArray(log.turns) ? log.turns : [];
  for (const t of turns) {
    for (const c of t && Array.isArray(t.commands) ? t.commands : []) {
      if (c && c.frame && c.frame.surrender) return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------- battle.finish -> FWP.end_game

/**
 * FWP.end_game of a finished server battle: the end-game object the server built (BattleFinishCommandResult.endGame:
 * the win, new level and surprise screens) with the graves the server keeps for grave.search (result.graves, in the
 * original [count, {name, cpu_level}, ...] form). `local` is the object end_game_fn prepared (win/lose, no bonuses):
 * what a battle the server refused shows (a loss).
 */
export function serverEndGame(result: any, local: any): any {
  const eg: any = result && result.endGame ? JSON.parse(JSON.stringify(result.endGame)) : { ...local, "win": false };
  const graves: any[] = [0, {}];
  const list: any[] = result && Array.isArray(result.graves) ? result.graves : [];
  for (const g of list) {
    ++graves[0];
    graves[graves[0]] = { "name": String(g.name), "cpu_level": Number(g.cpu_level) };
  }
  eg.graves = graves;
  return eg;
}
